import React, { useState, useEffect, useRef } from 'react'
import { messagingService } from '../lib/messagingService'
import { User } from './AuthContext'

interface Participant {
  id: string
  name: string
  outlet?: string
  email: string
  role: string
}

interface AddParticipantsProps {
  threadId: string
  threadTitle: string
  currentUser: User
  onParticipantsAdded: () => void
  onCancel: () => void
}

export const AddParticipants: React.FC<AddParticipantsProps> = ({
  threadId,
  threadTitle,
  currentUser,
  onParticipantsAdded,
  onCancel
}) => {
  const [currentParticipants, setCurrentParticipants] = useState<Participant[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<Array<{id: string, name: string, outlet: string, email: string}>>([])
  const [selectedJournalists, setSelectedJournalists] = useState<Array<{id: string, name: string, outlet: string, email: string}>>([])
  const [isSearching, setIsSearching] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isAdding, setIsAdding] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  
  const searchInputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Load current participants
  useEffect(() => {
    const loadParticipants = async () => {
      setIsLoading(true)
      const participants = await messagingService.getThreadParticipants(threadId)
      setCurrentParticipants(participants)
      setIsLoading(false)
    }
    
    loadParticipants()
  }, [threadId])

  // Search journalists with debounce
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchQuery.trim().length >= 2) {
        setIsSearching(true)
        const results = await messagingService.searchJournalists(searchQuery)
        
        // Filter out already selected journalists and current participants
        const currentParticipantIds = new Set(currentParticipants.map(p => p.id))
        const selectedIds = new Set(selectedJournalists.map(j => j.id))
        
        const filteredResults = results.filter(
          result => !currentParticipantIds.has(result.id) && !selectedIds.has(result.id)
        )
        
        setSearchResults(filteredResults)
        setShowDropdown(true)
        setIsSearching(false)
      } else {
        setSearchResults([])
        setShowDropdown(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery, selectedJournalists, currentParticipants])

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleAddJournalist = (journalist: {id: string, name: string, outlet: string, email: string}) => {
    setSelectedJournalists(prev => [...prev, journalist])
    setSearchQuery('')
    setSearchResults([])
    setShowDropdown(false)
    searchInputRef.current?.focus()
  }

  const handleRemoveJournalist = (journalistId: string) => {
    setSelectedJournalists(prev => prev.filter(j => j.id !== journalistId))
  }

  const handleAddParticipants = async () => {
    if (selectedJournalists.length === 0) return
    
    setIsAdding(true)
    
    try {
      const success = await messagingService.addParticipantsToThread(
        threadId,
        selectedJournalists.map(j => j.id),
        currentUser.id
      )
      
      if (success) {
        onParticipantsAdded()
      } else {
        alert('Failed to add participants. Please try again.')
      }
    } catch (error) {
      console.error('Error adding participants:', error)
      alert('Failed to add participants. Please try again.')
    } finally {
      setIsAdding(false)
    }
  }

  const canAdd = selectedJournalists.length > 0 && !isAdding

  // Count participants by role
  const journalistCount = currentParticipants.filter(p => p.role === 'journalist').length
  const prCount = currentParticipants.filter(p => ['pr', 'admin'].includes(p.role)).length

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Manage Participants</h2>
          <p className="text-sm text-gray-600 mt-1">{threadTitle}</p>
        </div>

        {/* Current Participants */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-700">Current Participants</h3>
            <div className="text-xs text-gray-500">
              {journalistCount} journalist{journalistCount !== 1 ? 's' : ''}, {prCount} PR team
            </div>
          </div>
          
          {isLoading ? (
            <div className="text-center py-4 text-gray-500">Loading participants...</div>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {/* PR Team Members */}
              {currentParticipants
                .filter(p => ['pr', 'admin'].includes(p.role))
                .map((participant) => (
                  <div key={participant.id} className="flex items-center justify-between bg-blue-50 p-2 rounded-md">
                    <div>
                      <div className="font-medium text-sm">{participant.name}</div>
                      <div className="text-xs text-gray-600">PR Team</div>
                    </div>
                  </div>
                ))
              }
              
              {/* Journalists */}
              {currentParticipants
                .filter(p => p.role === 'journalist')
                .map((participant) => (
                  <div key={participant.id} className="flex items-center justify-between bg-gray-50 p-2 rounded-md">
                    <div>
                      <div className="font-medium text-sm">{participant.name}</div>
                      <div className="text-xs text-gray-600">{participant.outlet || 'Journalist'}</div>
                    </div>
                  </div>
                ))
              }
            </div>
          )}
        </div>

        {/* Add New Participants */}
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Add Journalists
            </label>
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, outlet, or email..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              
              {/* Search Dropdown */}
              {showDropdown && (
                <div 
                  ref={dropdownRef}
                  className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg max-h-40 overflow-y-auto z-10 mt-1"
                >
                  {isSearching ? (
                    <div className="p-3 text-center text-gray-500">Searching...</div>
                  ) : searchResults.length > 0 ? (
                    searchResults.map((journalist) => (
                      <button
                        key={journalist.id}
                        onClick={() => handleAddJournalist(journalist)}
                        className="w-full p-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">{journalist.name}</div>
                        <div className="text-sm text-gray-600">{journalist.outlet}</div>
                      </button>
                    ))
                  ) : searchQuery.trim().length >= 2 ? (
                    <div className="p-3 text-center text-gray-500">No journalists found</div>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          {/* Selected Journalists to Add */}
          {selectedJournalists.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Journalists to Add ({selectedJournalists.length})
              </label>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {selectedJournalists.map((journalist) => (
                  <div 
                    key={journalist.id}
                    className="flex items-center justify-between bg-green-50 p-2 rounded-md"
                  >
                    <div>
                      <div className="font-medium text-sm">{journalist.name}</div>
                      <div className="text-xs text-gray-600">{journalist.outlet}</div>
                    </div>
                    <button
                      onClick={() => handleRemoveJournalist(journalist.id)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Remove journalist"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onCancel}
            disabled={isAdding}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleAddParticipants}
            disabled={!canAdd}
            className="px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isAdding ? 'Adding...' : `Add ${selectedJournalists.length} Participant${selectedJournalists.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  )
}